// ─── src/stores/auraCore.ts ───────────────────────────────────────────────────
// AuraCore: single source of truth for all Aura state.
//
// Tab isolation contract:
//   - session.id is unique per tab (generated in sessionStorage, never shared)
//   - conversation state (messages, intent, recommendations) is tab-local
//   - preferences sync across tabs via localStorage + storage event listener
//
// Persistence contract:
//   - chatUiState → sessionStorage (clears when tab closes, intentional)
//   - messages → localStorage (persists across refreshes, last 100 kept)
//   - preferences → localStorage key 'lwd:aura:prefs' (cross-tab sync)

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { queryAuraOrchestrator } from '../services/auraClient';
import { streamAuraOrchestrator } from '../services/auraStreamClient';
import { getOrCreateAnonId }     from '../services/auraIdentity';
import { getAuraTouch }        from '../services/auraAttribution';
import { categoryLabel } from '../utils/transformListing';
import { supabase } from '../lib/supabaseClient';
import { createWeddingStore, assembleFromTurn, roleFromCategory } from '../services/weddingStore';
import { parseWeddingTiming, approxDateFromMonths } from '../utils/weddingTiming';
import { buildJourney } from '../utils/weddingJourney';
import { isPreviewPrivate } from '../chat/previewMode';
import { cleanDashes } from '../utils/cleanText';

// ─── The Wedding entity (feature-flagged) ─────────────────────────────────────
// Dormant unless VITE_WEDDING_ENTITY === 'on' (absent on prod). The ?aura=private
// preview flag also enables it, scoped to that browser, so Private can be tested on
// prod without turning the whole entity on for everyone. See project_wedding_entity
// / project_two_aura_products.
const WEDDING_ENTITY_ENABLED = import.meta.env.VITE_WEDDING_ENTITY === 'on' || isPreviewPrivate();
const weddingStore = WEDDING_ENTITY_ENABLED ? createWeddingStore(supabase) : null;

import type {
  AuraState,
  AuraActions,
  AuraIntent,
  AuraMessage,
  AuraListing,
  AuraRecommendations,
  AuraActiveContext,
  AuraPreferences,
  AuraAnalyticsEvent,
  IntentMetrics,
  UseAuraReturn,
} from './auraCore.types';

import {
  AURA_INIT_MESSAGE,
  AURA_DEFAULT_PREFERENCES,
  AURA_DEFAULT_RECOMMENDATIONS,
  AURA_DEFAULT_CONTEXT,
} from './auraCore.types';

// ─── Aura shortlist storage (session-scoped) ─────────────────────────────────
// Separate from the global "lwd_shortlist" (which persists cross-session).
// This one lives in sessionStorage, scoped to the current conversation.

const AURA_SHORTLIST_KEY  = 'lwd:aura:shortlist';
const AURA_ENQUIRED_KEY   = 'lwd:aura:enquired_ids';

function loadAuraShortlist(): AuraListing[] {
  try { return JSON.parse(sessionStorage.getItem(AURA_SHORTLIST_KEY) ?? '[]'); }
  catch { return []; }
}

function saveAuraShortlist(items: AuraListing[]): void {
  try { sessionStorage.setItem(AURA_SHORTLIST_KEY, JSON.stringify(items)); }
  catch {}
}

function loadEnquiredIds(): string[] {
  try { return JSON.parse(localStorage.getItem(AURA_ENQUIRED_KEY) ?? '[]'); }
  catch { return []; }
}

function saveEnquiredIds(ids: string[]): void {
  try { localStorage.setItem(AURA_ENQUIRED_KEY, JSON.stringify(ids)); }
  catch {}
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  return w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
}

function loadMessages(): AuraMessage[] | null {
  try { return JSON.parse(localStorage.getItem('lwd_chat_messages') ?? 'null'); }
  catch { return null; }
}

function saveMessages(msgs: AuraMessage[]): void {
  try { localStorage.setItem('lwd_chat_messages', JSON.stringify(msgs.slice(-100))); }
  catch {}
}

// ── Saved conversations ────────────────────────────────────────────────────────
// "New Chat" preserves the current wedding (as an Earlier idea) AND its chat thread,
// so reopening it restores the full conversation, not just the canvas. Threads are
// keyed by wedding id in localStorage; the wedding itself lives in the DB.
const CONVERSATIONS_KEY = 'lwd:aura:conversations';
function saveConversationThread(weddingId: string, msgs: AuraMessage[]): void {
  if (!weddingId) return;
  try {
    const all = JSON.parse(localStorage.getItem(CONVERSATIONS_KEY) ?? '{}');
    all[weddingId] = { messages: (msgs ?? []).slice(-100), savedAt: new Date().toISOString() };
    localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(all));
  } catch {}
}
function loadConversationThread(weddingId: string): AuraMessage[] | null {
  if (!weddingId) return null;
  try { return JSON.parse(localStorage.getItem(CONVERSATIONS_KEY) ?? '{}')[weddingId]?.messages ?? null; }
  catch { return null; }
}

function loadChatUiState(): AuraState['chatUiState'] {
  try {
    // Purge any legacy localStorage value. A prior build persisted chatUiState in
    // localStorage, which survives browser restarts, so a left-open full Aura
    // reopened and took over the homepage on every visit until the user wiped site
    // data. chatUiState is sessionStorage-scoped (clears when the tab closes) so a
    // fresh visit never auto-launches the takeover. Messages persist separately, so
    // reopening still shows the conversation.
    localStorage.removeItem('lwd_chat_ui_state');
    const v = sessionStorage.getItem('lwd_chat_ui_state');
    // Never auto-launch the FULL takeover on a page load. Restore at most the mini
    // bar; the user re-expands to full deliberately. This guarantees the homepage is
    // never replaced by full Aura on a fresh render.
    if (v === 'open' || v === 'fullchat') return 'open';
  } catch {}
  return 'closed';
}

function loadPreferences(): AuraPreferences {
  try {
    const raw = localStorage.getItem('lwd:aura:prefs');
    if (raw) return { ...AURA_DEFAULT_PREFERENCES, ...JSON.parse(raw) };
  } catch {}
  return { ...AURA_DEFAULT_PREFERENCES };
}

function savePreferences(prefs: AuraPreferences): void {
  try { localStorage.setItem('lwd:aura:prefs', JSON.stringify(prefs)); }
  catch {}
}

function loadChatDark(): boolean {
  try {
    const v = localStorage.getItem('lwd:aura:chatDark');
    if (v === 'false') return false;
    if (v === 'true')  return true;
  } catch {}
  return true; // default: dark
}

function saveChatDark(dark: boolean): void {
  try { localStorage.setItem('lwd:aura:chatDark', String(dark)); }
  catch {}
}

// ─── Tab-scoped session ID ────────────────────────────────────────────────────
// Stored in sessionStorage so each tab gets its own ID.
// Two tabs open = two independent sessions. Intentional.

function getOrCreateTabSessionId(): string {
  try {
    let id = sessionStorage.getItem('lwd_session_id');
    if (!id || !id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      id = crypto.randomUUID();
      sessionStorage.setItem('lwd_session_id', id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

// ─── Fallback AI responses ────────────────────────────────────────────────────

const FALLBACKS = [
  "I've updated the recommendations on the right. Take a look at a few venues that match what you're describing.",
  "Our curated selection reflects your search. Let me know if you'd like to refine further.",
  "Every venue in our collection offers full exclusive use. I've surfaced the best matches on the right.",
];
let _fallbackIdx = 0;
const nextFallback = () => FALLBACKS[_fallbackIdx++ % FALLBACKS.length];

// "Welcome back", a grounded resume greeting for a returning couple whose
// wedding persists. Names only what's real (destination + shortlisted entities),
// never fabricates progress.
function welcomeBackGreeting(w: any, team: any[]): string {
  // "on the ${destination}" produced "your wedding on the Italy" — the article was
  // hard-coded for destinations like "the Amalfi Coast" and applied to every country.
  // Only trailing landform words take an article: "the Amalfi Coast" but "Italy",
  // and "Lake Como" rather than "the Lake Como".
  const NEEDS_ARTICLE = /(coast|riviera|valley|islands?|highlands|cotswolds|dolomites|alps|algarve)$/i;
  const dest = w?.destination
    ? ` in ${NEEDS_ARTICLE.test(String(w.destination).trim()) ? 'the ' : ''}${w.destination}`
    : '';
  const named = (team || []).filter((t) => t?.entity_name).map((t) => t.entity_name);
  let progress = '';
  if (named.length === 1)      progress = ` ${named[0]} is on your shortlist.`;
  else if (named.length > 1)   progress = ` ${named.slice(0, 2).join(' and ')} are on your shortlist.`;
  return `Welcome back. I've been looking after your wedding${dest}.${progress} Shall we pick up where we left off?`;
}

// Wedding Memory: the "while you were away" opening. Reads when the couple last saw
// this wedding, asks the store how many new listings joined its destination since,
// and (only on a real return, never the first visit) leads with that news. Always
// stamps "now" as the new last-seen. Falls back to the plain welcome on any gap.
// hydrateWedding can fire more than once (mount, auth settle). Compose the
// "welcome back" exactly once per wedding per session, or a later pass would consume
// the freshly-stamped last-seen and overwrite the memory with the plain greeting.
const welcomedWeddings = new Set<string>();

async function composeWelcomeBack(w: any, team: any[], store: any): Promise<string> {
  const base = welcomeBackGreeting(w, team);
  const id = w?.id;
  if (!id || !store?.newListingsSince) return base;
  const key = `lwd:wedding:seen:${id}`;
  let lastSeen: string | null = null;
  try { lastSeen = localStorage.getItem(key); } catch {}
  try { localStorage.setItem(key, new Date().toISOString()); } catch {}
  if (!lastSeen || !w?.destination) return base;   // first visit → plain welcome, nothing to compare
  try {
    const n = await store.newListingsSince(w.destination, lastSeen);
    if (n > 0) {
      const dest = w.destination;
      return `Welcome back. While you were away, ${n} new ${n === 1 ? 'listing has' : 'listings have'} joined in ${dest} that could suit your wedding. Shall we take a look?`;
    }
  } catch { /* fall back to base */ }
  return base;
}

// ─── Store ────────────────────────────────────────────────────────────────────

const sessionId = getOrCreateTabSessionId();

// ─── Streaming wiring (project_aura_streaming_ux_improvement.md) ──────────────
// Client-side rollout switch. Independent of the server-side AURA_ENABLE_STREAMING
// kill switch (which works even if this is left on — see streamAuraOrchestrator's
// content-type fallback). Default false: an explicit build-time flip is required
// to turn streaming on for real traffic, so shipping this code does not itself
// change what any user sees. Streams against the SAME orchestrator URL every
// other call already uses — not a separate endpoint.
const AURA_STREAMING_ENABLED = import.meta.env.VITE_AURA_STREAMING_ENABLED === 'true';
// Dev-only escape hatch to point at a local orchestrator during testing without
// touching production Supabase. Never set in any deployed environment; when
// unset (always true in prod), this resolves to the exact same real URL every
// other Aura call already uses.
const AURA_STREAM_URL = (import.meta.env.VITE_AURA_STREAM_URL_OVERRIDE as string | undefined)
  || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/aura-orchestrator`;

export const useAura = create<AuraState & AuraActions>()(
  subscribeWithSelector((set, get) => ({
    // ── Initial state ──────────────────────────────────────────────────────────
    session: {
      id: sessionId,
      startTime: Date.now(),
      lastActivityTime: Date.now(),
    },
    messages: loadMessages() ?? [AURA_INIT_MESSAGE],
    isTyping: false,
    streamStage: null,
    streamingText: null,
    activeContext: { ...AURA_DEFAULT_CONTEXT },
    chatUiState: loadChatUiState(),
    chatDark: loadChatDark(),
    intent: null,
    intentMetrics: null,
    recommendations: { ...AURA_DEFAULT_RECOMMENDATIONS },
    recommendationsLoading: false,
    articleRecommendations: [],
    articlesLoading: false,
    preferences: loadPreferences(),
    analyticsBuffer: [],
    queryId: null,
    conversationMode: null,
    consecutiveClarificationCount: 0,
    activeVenueId: null,
    auraShortlistedIds: [],
    auraShortlistedItems: loadAuraShortlist(),
    enquiredVenueIds: loadEnquiredIds(),
    wedding: null,
    weddingTeam: [],
    weddingDismissedIds: [],
    weddingCandidates: {},
    // Preserved directions the Guardian kept safe (status='alternative'). Shown to
    // the couple as "Earlier ideas"; one tap reopens one. Never the word "scenario".
    weddingScenarios: [],
    // The event log rendered as the couple's notebook, day-grouped. Shown as
    // "Our Journey"; reads only what's logged, never an invented why.
    weddingJourney: [],
    // The entity the couple has selected in the wedding, drives the right column
    // to recompose into everything we already know about it (Entity Focus).
    focusedEntity: null,

    // ── UI actions ─────────────────────────────────────────────────────────────
    openMiniBar() {
      set({ chatUiState: 'open' });
      try { sessionStorage.setItem('lwd_chat_ui_state', 'open'); } catch {}
    },

    openWorkspace() {
      set({ chatUiState: 'fullchat' });
      try { sessionStorage.setItem('lwd_chat_ui_state', 'fullchat'); } catch {}
    },

    closeChat() {
      set({ chatUiState: 'closed' });
      try { sessionStorage.setItem('lwd_chat_ui_state', 'closed'); } catch {}
    },

    closeFull() {
      set({ chatUiState: 'open' });
      try { sessionStorage.setItem('lwd_chat_ui_state', 'open'); } catch {}
    },

    toggleTheme() {
      set((s) => {
        const next = !s.chatDark;
        saveChatDark(next);
        return { chatDark: next };
      });
    },

    // ── Context ────────────────────────────────────────────────────────────────
    setChatContext(ctx: AuraActiveContext) {
      const prev = get().activeContext;
      const changed =
        prev.country      !== ctx.country      ||
        prev.region       !== ctx.region       ||
        prev.page         !== ctx.page         ||
        prev.listingId    !== ctx.listingId    ||
        prev.categorySlug !== ctx.categorySlug;

      if (!changed) return;

      // Clear conversation when page or listing changes.
      // Guard: skip the clear when the store was just initialised (prev.page = null).
      // Without this guard every page refresh triggers a clear because the store
      // boots with page: null and the first setChatContext call always differs.
      const wasOnRealPage = prev.page !== null || prev.listingId !== null;
      const pageOrListingChanged =
        wasOnRealPage && (
          (prev.page      ?? null) !== (ctx.page      ?? null) ||
          (prev.listingId ?? null) !== (ctx.listingId ?? null)
        );

      if (pageOrListingChanged) {
        saveAuraShortlist([]);
        saveEnquiredIds([]);
        set({
          activeContext: ctx,
          messages: [AURA_INIT_MESSAGE],
          isTyping: false,
          recommendations: { ...AURA_DEFAULT_RECOMMENDATIONS },
          intent: null,
          intentMetrics: null,
          queryId: null,
          conversationMode: null,
          consecutiveClarificationCount: 0,
          activeVenueId: null,
          auraShortlistedIds: [],
          auraShortlistedItems: [],
          enquiredVenueIds: [],
        });
        try { localStorage.removeItem('lwd_chat_messages'); } catch {}
      } else {
        set({ activeContext: ctx });
      }
    },

    // ── Conversation ───────────────────────────────────────────────────────────
    sendMessage(text: string) {
      const trimmed = text.trim();
      if (!trimmed) return;

      const userMsg: AuraMessage = {
        id: Date.now(),
        from: 'user',
        text: trimmed,
        timestamp: Date.now(),
      };

      set((s) => {
        const next = [...s.messages, userMsg];
        saveMessages(next);
        return { messages: next, isTyping: true, streamStage: null, streamingText: null };
      });

      // Update session activity
      set((s) => ({ session: { ...s.session, lastActivityTime: Date.now() } }));

      // Fire and forget, async response handled below
      const currentMessages = get().messages;
      const activeContext = get().activeContext;
      const currentClarificationCount = get().consecutiveClarificationCount;

      // AURA_STREAMING_ENABLED is the client-side rollout switch (default off — an
      // explicit build-time flip is required). When false, this calls the exact
      // same _generateAuraResponse the app has always used, unchanged.
      const responsePromise = AURA_STREAMING_ENABLED
        ? _generateAuraResponseStreaming(trimmed, currentMessages, activeContext, currentClarificationCount)
        : _generateAuraResponse(trimmed, currentMessages, activeContext, currentClarificationCount);

      responsePromise
        .then(({ text: responseText, recommendations, _intent, _listingIds, _queryId, _conversationMode, _requiresClarification, _followUpPrompts, _editorialContext, _showcaseContext, _realWeddingContext, _trace, _reasoningId, _activeComparison, _productContext, _productBrowse }) => {
          const auraMsg: AuraMessage = {
            id: Date.now() + 1,
            from: 'aura',
            text: responseText,
            timestamp: Date.now(),
            follow_up_prompts: _followUpPrompts,
            editorial_context: _editorialContext,
            showcase_context: _showcaseContext,
            real_wedding_context: _realWeddingContext,
            _intent,
            _listingIds: _listingIds?.length ? _listingIds : undefined,
            _query_id:   _queryId ?? undefined,
            // Architecture/28: the active comparison rides on the aura message and is
            // echoed back verbatim on the next request. The client never inspects it.
            _activeComparison: _activeComparison ?? undefined,
            // Shop surface: products ride their own channel, never `recommendations` —
            // the rail must be able to tell a purchasable product from a member listing.
            product_context: Array.isArray(_productContext) && _productContext.length ? _productContext : undefined,
            product_browse: _productBrowse ?? undefined,
          };
          set((s) => {
            const next = [...s.messages, auraMsg];
            saveMessages(next);
            return { messages: next, isTyping: false, streamStage: null, streamingText: null };
          });
          // Keep existing sidebar recommendations when a follow-up turn returns
          // zero results, avoids blanking the sidebar mid-conversation.
          // Only update recommendations when the API returned actual listings.
          if (recommendations !== null) {
            const firstId = recommendations.items?.[0]?.id ?? null;
            const newIds  = new Set(recommendations.items.map((i) => i.id));
            set((s) => ({
              recommendations,
              recommendationsLoading: false,
              // Preserve the user's active selection if that venue is still in the
              // new listing set (e.g. follow-up about the same venues).
              // Only promote firstId when there is no current selection or the
              // previously active venue isn't in the returned set (fresh search).
              activeVenueId: (s.activeVenueId && newIds.has(s.activeVenueId))
                ? s.activeVenueId
                : firstId,
            }));
          } else {
            set({ recommendationsLoading: false });
          }
          set((s) => ({
            queryId: _queryId ?? s.queryId,
            conversationMode: _conversationMode ?? null,
            consecutiveClarificationCount: (_requiresClarification ?? false)
              ? s.consecutiveClarificationCount + 1
              : 0,
          }));
          // The Wedding entity, silently mint / update off this turn (flag-gated,
          // fire-and-forget; never blocks or breaks the chat). _trace (present only in
          // debug) carries the edge segment so the client can stitch on the promotion gate.
          get()._syncWeddingFromTurn(_intent, recommendations, _trace, _reasoningId);
        })
        .catch(() => {
          const auraMsg: AuraMessage = {
            id: Date.now() + 1,
            from: 'aura',
            text: nextFallback(),
            timestamp: Date.now(),
          };
          set((s) => {
            const next = [...s.messages, auraMsg];
            saveMessages(next);
            return { messages: next, isTyping: false, streamStage: null, streamingText: null };
          });
        });
    },

    clearHistory() {
      saveAuraShortlist([]);
      saveEnquiredIds([]);
      set({
        messages: [AURA_INIT_MESSAGE],
        isTyping: false,
        recommendations: { ...AURA_DEFAULT_RECOMMENDATIONS },
        intent: null,
        intentMetrics: null,
        queryId: null,
        conversationMode: null,
        consecutiveClarificationCount: 0,
        activeVenueId: null,
        auraShortlistedIds: [],
        auraShortlistedItems: [],
        enquiredVenueIds: [],
      });
      try { localStorage.removeItem('lwd_chat_messages'); } catch {}
    },

    // ── Intent ─────────────────────────────────────────────────────────────────
    setIntent(intent: AuraIntent, metrics?: IntentMetrics) {
      set({ intent, intentMetrics: metrics ?? null });
    },

    // ── Recommendations ────────────────────────────────────────────────────────
    setRecommendations(recs: AuraRecommendations) {
      set({ recommendations: recs, recommendationsLoading: false });
    },

    setRecommendationsLoading(loading: boolean) {
      set({ recommendationsLoading: loading });
    },

    setArticleRecommendations(articles: AuraListing[]) {
      set({ articleRecommendations: articles, articlesLoading: false });
    },

    // ── Preferences (global, cross-tab) ───────────────────────────────────────
    setPreference<K extends keyof AuraPreferences>(key: K, value: AuraPreferences[K]) {
      set((s) => {
        const next = { ...s.preferences, [key]: value };
        savePreferences(next);
        return { preferences: next };
      });
    },

    dismissPrompt(id: string) {
      set((s) => {
        if (s.preferences.dismissedPrompts.includes(id)) return s;
        const next = {
          ...s.preferences,
          dismissedPrompts: [...s.preferences.dismissedPrompts, id],
        };
        savePreferences(next);
        return { preferences: next };
      });
    },

    // ── Venue-Conversation Integration ─────────────────────────────────────────

    setActiveVenueId(id: string | null) {
      const prevId = get().activeVenueId;
      if (id === prevId) return;

      // When the focus switches to a different venue mid-conversation, inject a
      // compact system note so the transcript reflects the context change.
      if (id && prevId) {
        const item = get().recommendations.items.find((r) => r.id === id);
        if (item) {
          const noteMsg: AuraMessage = {
            id: Date.now(),
            from: 'aura',
            text: item.name,
            timestamp: Date.now(),
            _systemNote: true,
            _systemNoteText: item.name,
          };
          set((s) => {
            const next = [...s.messages, noteMsg];
            saveMessages(next);
            return { messages: next };
          });
        }
      }

      set({ activeVenueId: id });
    },

    // Mobile card tap, focus this recommendation and reply immediately with a
    // concise, data-bound intro (no LLM call). The tap IS the request for more,
    // so Aura leads with useful facts rather than asking the user to type.
    focusRecommendation(id: string) {
      const item = get().recommendations.items.find((r) => r.id === id);
      if (!item) return;

      // Idempotent, a repeated or delayed tap on the same card must not stack
      // duplicate intros. If we already introduced this listing as the most
      // recent Aura message, just re-focus and stop.
      const msgs = get().messages;
      const last = msgs[msgs.length - 1];
      if (last?._recIntroFor === id) {
        set({ activeVenueId: id });
        return;
      }

      // Build the intro purely from recommendation data (accuracy-first, no
      // fabrication). item.reason is Aura's own relevance explanation.
      // Dedupe city/region, some listings carry the same value for both
      // (e.g. "Amalfi Coast, Amalfi Coast"); collapse to one.
      const loc    = [...new Set([item.city, item.region].filter(Boolean))].join(', ');
      const detail = item.reason ? cleanDashes(String(item.reason)).trim() : '';
      const where  = loc ? `Based in ${loc}.` : '';
      const price  = item.priceFrom ? `Planning from ${item.priceFrom}.` : '';
      const lead   = `${item.name} is a strong match.`;
      const body   = [detail, where, price].filter(Boolean).join(' ');
      const text   = [lead, body].filter(Boolean).join('\n');

      const introMsg: AuraMessage = {
        id: Date.now(),
        from: 'aura',
        text,
        timestamp: Date.now(),
        _recIntroFor: id,
      };

      set((s) => {
        const next = [...s.messages, introMsg];
        saveMessages(next);
        return { messages: next, activeVenueId: id };
      });
    },

    auraShortlistVenue(item: AuraListing) {
      const { auraShortlistedItems } = get();
      if (!item?.id) return;
      if (auraShortlistedItems.some((x) => x.id === item.id)) return;
      if (auraShortlistedItems.length >= 3) return;

      const newItems = [...auraShortlistedItems, item];
      const newIds   = newItems.map((x) => x.id);
      saveAuraShortlist(newItems);

      // Deterministic acknowledgement, no LLM call, client-side only
      const count = newItems.length;
      let ackText = '';
      let ackPrompts: string[] | undefined;

      if (count === 1) {
        ackText = `${item.name} is on your shortlist.`;
      } else if (count === 2) {
        ackText = `I've added ${item.name} alongside ${auraShortlistedItems[0].name}. Two strong contenders.`;
      } else if (count === 3) {
        const names = newItems.map((x) => x.name);
        ackText = `Your shortlist is complete, ${names[0]}, ${names[1]}, and ${names[2]}. Ready to reach out to all three at once?`;
        ackPrompts = ['Enquire to all three', 'Compare the three venues'];
      }

      if (ackText) {
        const ackMsg: AuraMessage = {
          id: Date.now(),
          from: 'aura',
          text: ackText,
          timestamp: Date.now(),
          follow_up_prompts: ackPrompts,
        };
        set((s) => {
          const next = [...s.messages, ackMsg];
          saveMessages(next);
          return { messages: next };
        });
      }

      set({ auraShortlistedIds: newIds, auraShortlistedItems: newItems });
      // Wedding: shortlisting is a real signal, advance this role to Shortlisted.
      get()._advanceTeamRole(item, 'shortlisted');
    },

    auraShortlistRemove(id: string) {
      const newItems = get().auraShortlistedItems.filter((x) => x.id !== id);
      const newIds   = newItems.map((x) => x.id);
      saveAuraShortlist(newItems);
      set({ auraShortlistedIds: newIds, auraShortlistedItems: newItems });
    },

    clearAuraShortlist() {
      saveAuraShortlist([]);
      set({ auraShortlistedIds: [], auraShortlistedItems: [] });
    },

    markVenueEnquired(id: string) {
      set((s) => {
        if (s.enquiredVenueIds.includes(id)) return s;
        const next = [...s.enquiredVenueIds, id];
        saveEnquiredIds(next);
        return { enquiredVenueIds: next };
      });
      // Wedding: an enquiry is the strongest signal short of booking → Enquired.
      const item = get().recommendations.items.find((i) => i.id === id)
        ?? get().auraShortlistedItems.find((i) => i.id === id);
      if (item) get()._advanceTeamRole(item, 'enquired');
    },

    // ── The Wedding entity ─────────────────────────────────────────────────────
    // Resume: load the active wedding + team on startup (the "welcome back").
    async hydrateWedding() {
      if (!weddingStore) return;
      try {
        const w = await weddingStore.getActiveWedding();
        if (w) {
          const team = await weddingStore.getTeam((w as any).id);
          const dismissed = await weddingStore.getRejectedEntityIds((w as any).id);
          // Rehydrate the active suggested/exploring candidate so the gold box
          // survives reload, matched to the team's current entity, not settled,
          // not rejected.
          const suggested = await weddingStore.getSuggestedCandidates((w as any).id);
          const SETTLED = ['shortlisted', 'enquired', 'booked'];
          const candidates: Record<string, { item: Record<string, unknown>; reasoning: Record<string, unknown> }> = {};
          for (const t of (team as any[])) {
            if (SETTLED.includes(t.state)) continue;
            const p = suggested[t.role];
            if (!p || p.entity_id !== t.entity_id || dismissed.includes(p.entity_id)) continue;
            candidates[t.role] = {
              item: { id: p.entity_id, name: p.entity_name, slug: p.slug, category: p.category, categorySlug: p.category, type: p.entity_type, countrySlug: p.country_slug, regionSlug: p.region_slug, imgs: p.image ? [p.image] : [] },
              reasoning: { reasons: Array.isArray(p.reasons) ? p.reasons : [], entity_id: p.entity_id, entity_name: p.entity_name },
            };
          }
          set({ wedding: w, weddingTeam: team, weddingDismissedIds: dismissed, weddingCandidates: candidates });
          void get().refreshScenarios();
          void get().refreshJourney();
          // Welcome back: personalise the opening greeting when returning to a
          // fresh thread (only the initial greeting, no conversation yet).
          const msgs = get().messages;
          if (msgs.length === 1 && msgs[0]?.id === 0 && !welcomedWeddings.has((w as any).id)) {
            welcomedWeddings.add((w as any).id);   // claim synchronously before the await, so a parallel hydrate skips
            const greeting = await composeWelcomeBack(w, team, weddingStore);
            const cur = get().messages;   // guard: the couple may have typed during the await
            if (cur.length === 1 && cur[0]?.id === 0) {
              const next = [{ ...cur[0], text: greeting }];
              saveMessages(next);
              set({ messages: next });
            }
          }
        } else {
          // No wedding for the current identity (e.g. after sign-out), clear.
          set({ wedding: null, weddingTeam: [], weddingDismissedIds: [], weddingCandidates: {}, weddingScenarios: [] });
        }
      } catch { /* dormant / offline, ignore */ }
    },

    // Silent assembly: mint at confidence >= 60, then keep DNA + team in sync
    // with the conversation. Fire-and-forget; any failure must never touch chat.
    async _syncWeddingFromTurn(intent, recommendations, edgeTrace, reasoningId) {
      if (!weddingStore || !intent) return;
      try {
        const { wedding, team, destinationChanged, forked, promotion } = await assembleFromTurn(weddingStore, get().wedding as any, intent, recommendations, reasoningId);
        // Reasoning trace (debug only): stitch the client segment, the promotion gate,
        // the last seam before the canvas, onto the edge segment for the full replay,
        // then expose the whole turn at window.__auraLastTrace, keyed by its Reasoning ID.
        // Best-effort, never throws.
        if (Array.isArray(edgeTrace) && typeof window !== 'undefined') {
          try {
            const p = promotion as Record<string, unknown> | undefined;
            const clientStage = {
              stage: 'promotion',
              summary: p?.promoted ? `promoted ${p.entity} → ${p.role}` : `nothing promoted (${p?.reason ?? 'gate rejected'})`,
              data: p,
              flags: p && p.eligible === false ? ['gate_rejected_ineligible'] : [],
            };
            (window as unknown as { __auraLastTrace?: unknown }).__auraLastTrace = { reasoning_id: reasoningId ?? null, stages: [...edgeTrace, clientStage] };
            console.log(`[AURA trace] ${reasoningId ?? ''}`, (window as unknown as { __auraLastTrace?: unknown }).__auraLastTrace);
          } catch { /* trace is best-effort */ }
        }
        if (wedding) set({ wedding, weddingTeam: team });
        // The heartbeat: capture the couple's stated timing from their OWN words,
        // honest, only when they actually say a "when". Stored as an (approx) date so
        // the horizon stays fresh, plus their label shown verbatim.
        if (wedding) {
          const lastUser = [...get().messages].reverse().find((m) => m.from === 'user');
          const timing = lastUser?.text ? parseWeddingTiming(lastUser.text) : null;
          if (timing) {
            const storeDate = timing.date ?? approxDateFromMonths(timing.monthsOut);
            if (storeDate !== (wedding as any).wedding_date || timing.label !== (wedding as any).date_label) {
              const updated = await weddingStore.setWeddingDate((wedding as any).id, { date: storeDate, label: timing.label });
              if (updated) set({ wedding: updated });
            }
          }
        }
        // Guardian: a pivot preserved committed work. Say so, quietly, and never as
        // "scenario", the old direction is safe and one restore away. Otherwise a
        // plain edit-in-place gets the subtle "Wedding updated" note.
        const pushNote = (txt: string) => {
          const note: AuraMessage = { id: Date.now() + 2, from: 'aura', text: txt, timestamp: Date.now(), _systemNote: true, _systemNoteText: txt };
          set((s) => { const next = [...s.messages, note]; saveMessages(next); return { messages: next }; });
        };
        if (forked) {
          pushNote(`Kept safe · ${forked.keptLabel || 'your earlier idea'}`);
          void get().refreshScenarios();
        } else if (destinationChanged) {
          pushNote(`Wedding updated · ${destinationChanged.to}`);
        }
        void get().refreshJourney();   // every turn deposits events → keep the notebook fresh
      } catch { /* never break the chat over wedding sync */ }
    },

    // Preserved directions (status='alternative') for the "Earlier ideas" surface.
    // Excludes the active wedding. Dormant/offline safe.
    async refreshScenarios() {
      if (!weddingStore) return;
      try {
        const all = await weddingStore.listScenarios();
        const active = get().wedding as any;
        set({ weddingScenarios: (all || []).filter((w: any) => w.status === 'alternative' && w.id !== active?.id) });
      } catch { /* dormant / offline, ignore */ }
    },

    // Our Journey, read the active wedding's event log back as day-grouped sentences.
    async refreshJourney() {
      if (!weddingStore) return;
      try {
        const w = get().wedding as any;
        if (!w?.id) { set({ weddingJourney: [] }); return; }
        const events = await weddingStore.getWeddingJourney(w.id);
        set({ weddingJourney: buildJourney(events) });
      } catch { /* dormant / offline, ignore */ }
    },

    // Reopen a preserved direction: the current active becomes an alternative and
    // the chosen one becomes active. Nothing is rebuilt or re-searched, hydrate
    // reloads the now-active wedding, team and candidates as they were left.
    async restoreScenario(id: string) {
      if (!weddingStore || !id) return;
      try {
        const restored = await weddingStore.restoreScenario(id);
        if (!restored) return;
        await get().hydrateWedding();   // reloads active wedding + team (+ refreshScenarios)
        const label = (restored as any).destination || (restored as any).label || 'your earlier idea';
        // If this wedding was set aside via New Chat, its full conversation was saved,
        // bring the whole thread back. Otherwise just mark the reopen in the current chat.
        const savedThread = loadConversationThread(id);
        if (savedThread && savedThread.length) {
          saveMessages(savedThread);
          set({ messages: savedThread });
        } else {
          const note: AuraMessage = { id: Date.now() + 4, from: 'aura', text: `Opened · ${label}`, timestamp: Date.now(), _systemNote: true, _systemNoteText: `Opened · ${label} direction` };
          set((s) => { const next = [...s.messages, note]; saveMessages(next); return { messages: next }; });
        }
      } catch { /* never break the chat */ }
    },

    // New Chat: save the current wedding + its conversation so nothing is lost, then
    // clear BOTH the chat and the canvas for a fresh start. The saved wedding returns
    // via "Earlier ideas" (one tap reopens the canvas AND the full thread). If there's
    // no wedding yet, this is just a plain chat clear.
    async saveAndStartNewChat() {
      const w = get().wedding as any;
      if (w?.id) {
        saveConversationThread(w.id, get().messages);
        try { if (weddingStore) await weddingStore.preserveActive(); } catch { /* keep going, still clear locally */ }
      }
      get().clearHistory();
      set({ wedding: null, weddingTeam: [], weddingJourney: [], weddingCandidates: {}, weddingDismissedIds: [] });
      try { await get().refreshScenarios(); } catch { /* the saved wedding still shows on next hydrate */ }
    },

    // Advance a team role on a real user action (shortlist → Shortlisted,
    // enquiry → Enquired). The store never regresses, so this only ever moves a
    // role forward. No-op unless a wedding already exists (flag-gated store).
    async _advanceTeamRole(item, state) {
      if (!weddingStore || !item) return;
      try {
        const w = get().wedding as any;
        if (!w) return;
        const role = roleFromCategory(item.categorySlug ?? item.category);
        // Remember the role's tier BEFORE the write, so we only surface a chat
        // moment on a genuine forward step (added → enquiry → booked), never twice.
        const RANK: Record<string, number> = { shortlisted: 1, enquired: 2, booked: 3 };
        const prev = (get().weddingTeam || []).find((t: any) => t.role === role) as any;
        const prevRank = prev ? (RANK[prev.state] ?? 0) : 0;
        await weddingStore.upsertTeamRole(w.id, role, {
          state,
          entityId: item.id,
          entityType: item.type,
          entityName: item.name,
          source: state === 'enquired' ? 'user_enquiry' : 'user_focused',
          confidence: item.lwdScore ?? null,
        });
        set({ weddingTeam: await weddingStore.getTeam(w.id) });
        // A quiet system moment that ties the chat to the wedding, the chat EDITS
        // the wedding, it does not just reply. Rendered as a compact gold divider
        // (not a bubble), same as the destination-pivot note. Only ever in Private
        // (guarded by the wedding existing above).
        if ((RANK[state] ?? 0) > prevRank) {
          const ROLE_LABEL: Record<string, string> = { venue: 'Venue', planner: 'Planner', photographer: 'Photographer', videographer: 'Videographer', florist: 'Florist', catering: 'Food & Drink', entertainment: 'Entertainment', celebrant: 'Celebrant', vendor: 'Supplier' };
          const label = ROLE_LABEL[role] || 'Supplier';
          const txt = state === 'enquired'
            ? `Enquiry sent to your ${label.toLowerCase()}`
            : state === 'booked'
              ? `${label} booked`
              : `${label} added to your wedding`;
          const note: AuraMessage = { id: Date.now() + 3, from: 'aura', text: txt, timestamp: Date.now(), _systemNote: true, _systemNoteText: `✓ ${txt}` };
          set((s) => { const next = [...s.messages, note]; saveMessages(next); return { messages: next }; });
        }
      } catch { /* never break the chat */ }
    },

    // A couple dismissing a candidate ("Not for us"). Deposits a negative-preference
    // event (grounded evidence, Principle 7) captured from an action they already
    // take (Principle 8), and persists the dismissal so it survives reload. No-op
    // without a wedding, the rail falls back to in-session dismissal.
    async rejectCandidate(item, opts?: { reason?: string; reasonCategory?: string }) {
      if (!weddingStore || !item) return;
      try {
        const w = get().wedding as any;
        if (!w) return;
        await weddingStore.logRejection(w.id, {
          role: roleFromCategory((item as any).categorySlug ?? (item as any).category),
          entityId: (item as any).id,
          entityName: (item as any).name,
          // Optional, carried through only if a reason is ever supplied at rejection.
          // Existing single-arg callers (WorkspaceRight "Not for us") pass nothing → no reason.
          reason: opts?.reason ?? null,
          reasonCategory: opts?.reasonCategory ?? null,
        });
        set((s) => ({
          weddingDismissedIds: s.weddingDismissedIds.includes((item as any).id)
            ? s.weddingDismissedIds
            : [...s.weddingDismissedIds, (item as any).id],
        }));
      } catch { /* never break the chat */ }
    },

    // Entity Focus, the couple selected an entity in the wedding. The right column
    // recomposes into everything LWD already knows about it. Toggling the same
    // entity clears the focus (back to the query knowledge).
    focusEntity(entity) {
      if (!entity?.id) return;
      set((s) => ({ focusedEntity: s.focusedEntity?.id === entity.id ? null : entity }));
    },
    clearEntityFocus() { set({ focusedEntity: null }); },

    // Read-back for the evidence panel, the reasons + journey timeline behind a
    // team member. Silent capture made transparent (the Constitution). Returns empty
    // shapes when dormant/offline so the panel degrades gracefully.
    async getTeamEvidence(entityId) {
      const empty = { reasons: [], timeline: [] };
      if (!weddingStore || !entityId) return empty;
      try {
        const w = get().wedding as any;
        if (!w) return empty;
        return await weddingStore.getTeamMemberEvidence(w.id, entityId);
      } catch { return empty; }
    },

    // Persist the full reasoning object (positive reasons + concerns) as evidence
    // when a candidate is shortlisted. The Evidence engine, seeded from the rail.
    async logRecommendationReasoning(reasoning) {
      if (!weddingStore || !reasoning) return;
      try {
        const w = get().wedding as any;
        if (!w) return;
        await weddingStore.logEvent(w.id, 'recommendation_reasoned', reasoning, 'aura');
      } catch { /* non-blocking */ }
    },

    // ── Telemetry ──────────────────────────────────────────────────────────────
    trackEvent(type: string, payload: Record<string, unknown>) {
      const event: AuraAnalyticsEvent = {
        type,
        payload,
        sessionId,
        timestamp: Date.now(),
        device: getDevice(),
      };
      set((s) => ({ analyticsBuffer: [...s.analyticsBuffer, event] }));
    },

    flushAnalytics(): AuraAnalyticsEvent[] {
      const buffer = get().analyticsBuffer;
      set({ analyticsBuffer: [] });
      return buffer;
    },
  }))
);

// ─── Cross-tab preference sync ────────────────────────────────────────────────
// When preferences change in another tab, update this tab's store.
// Conversation state is intentionally NOT synced.

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'lwd:aura:prefs' || !e.newValue) return;
    try {
      const prefs = JSON.parse(e.newValue);
      useAura.setState((s) => ({
        preferences: { ...s.preferences, ...prefs },
      }));
    } catch {}
  });
}

// ─── PII redaction (deterministic, runs before telemetry storage) ─────────────

const EMAIL_RE  = /\b[^\s@]{1,64}@[^\s@]{1,253}\.[^\s@]{1,63}\b/g;
const PHONE_RE  = /(\+?\d[\d\s\-().]{6,}\d)/g;

function redactPii(text: string): string {
  return text
    .replace(EMAIL_RE, '[email]')
    .replace(PHONE_RE, (match) => {
      // Only replace if it contains enough digit density to be a phone number
      const digits = match.replace(/\D/g, '');
      return digits.length >= 7 ? '[phone]' : match;
    });
}

// ─── AI response (internal, not exported) ────────────────────────────────────
// Mirrors ChatContext.generateAuraResponse, will be the migration target.

// Engineering-only debug switch. When on, Aura asks the orchestrator for the reasoning
// trace and the client stitches its own segment (the promotion gate) onto it, exposing
// the full per-turn replay at window.__auraLastTrace. Off by default, zero effect on
// bride traffic. Flip with localStorage.setItem('aura_debug','1') or window.__AURA_DEBUG=true.
function auraDebugOn(): boolean {
  try {
    return typeof window !== 'undefined'
      && ((window as unknown as { __AURA_DEBUG?: boolean }).__AURA_DEBUG === true
        || localStorage.getItem('aura_debug') === '1');
  } catch { return false; }
}

type AuraTurnResult = {
  text: string;
  recommendations: AuraRecommendations | null;
  _intent?: Record<string, unknown>;
  _listingIds?: string[];
  _queryId?: string;
  _conversationMode?: string;
  _requiresClarification?: boolean;
  _followUpPrompts?: string[];
  _editorialContext?: unknown;
  _showcaseContext?: unknown;
  _realWeddingContext?: unknown;
  _trace?: unknown[];
  _reasoningId?: string;
  _activeComparison?: unknown;
  _productContext?: unknown;
  _productBrowse?: unknown;
};

// Shared by both the blocking and streaming turn functions so the request the
// orchestrator sees is IDENTICAL either way — only the transport differs.
function buildOrchestratorRequest(
  userMessage: string,
  conversationHistory: AuraMessage[],
  consecutiveClarificationCount: number,
): Parameters<typeof queryAuraOrchestrator>[0] {
  // Build previous intent context from last aura message in history
  const lastAuraMsg = [...conversationHistory].reverse().find((m) => m.from === 'aura' && m._intent);
  const previousIntent = lastAuraMsg?._intent ?? undefined;
  // Architecture/28: echo the active comparison back verbatim. The orchestrator owns
  // its lifecycle entirely; the client is a courier, never an interpreter.
  const lastComparisonMsg = [...conversationHistory].reverse().find((m) => m.from === 'aura' && (m as any)._activeComparison !== undefined);
  const activeComparison = (lastComparisonMsg as any)?._activeComparison ?? undefined;

  // Only use the most recent turn's listing IDs, accumulating all turns pollutes the context
  const lastAuraWithListings = [...conversationHistory].reverse().find(
    (m) => m.from === 'aura' && m._listingIds?.length,
  );
  const previousListingIds = lastAuraWithListings?._listingIds ?? [];

  // Use the most recent aura response text so the model knows what it just asked
  const lastAuraMsgAny = [...conversationHistory].reverse().find((m) => m.from === 'aura' && m.text);
  const previousResponseText = lastAuraMsgAny?.text ?? undefined;

  // Last 8 turns (user + aura only) as a proper multi-turn thread.
  // Trimmed to 400 chars per turn to keep the token budget lean for Haiku intent parsing.
  // System notes and empty messages are excluded.
  const chatHistory = conversationHistory
    .filter(m => (m.from === 'user' || m.from === 'aura') && m.text && m.text.trim().length > 0)
    .slice(-8)
    .map(m => ({
      role: m.from === 'user' ? 'user' as const : 'assistant' as const,
      content: (m.text ?? '').slice(0, 400),
    }));

  // The bridge: the active Wedding as the PRIMARY input to Aura's reasoning. A compact
  // factual summary (destination, date, guests, style, team, what's kept safe, recent
  // history) so the orchestrator reasons about THIS wedding, not an isolated message.
  // undefined when there's no wedding (unregistered / pre-mint) → frame stays empty.
  const weddingContextStr: string | undefined = (() => {
    const s = useAura.getState();
    const w = s.wedding as any;
    if (!w) return undefined;
    const SETTLED = ['shortlisted', 'enquired', 'booked'];
    const team = ((s.weddingTeam as any[]) || []);
    const parts: string[] = [];
    const bits = [w.destination, w.guest_count ? `${w.guest_count} guests` : null, w.style, w.date_label ? `date ${w.date_label}` : null].filter(Boolean);
    if (bits.length) parts.push(`Active wedding: ${bits.join(', ')}.`);
    const settled = team.filter((t) => t.entity_name && SETTLED.includes(t.state)).map((t) => `${t.role} ${t.entity_name} (${t.state})`);
    if (settled.length) parts.push(`Team so far: ${settled.join('; ')}.`);
    const needed = ['venue', 'planner', 'photographer', 'florist'].filter((r) => !team.some((t) => t.role === r && SETTLED.includes(t.state)));
    if (needed.length) parts.push(`Still needed: ${needed.join(', ')}.`);
    const alts = (((s.weddingScenarios as any[]) || []).map((x) => x.label || x.destination).filter(Boolean));
    if (alts.length) parts.push(`Earlier directions kept safe: ${alts.join(', ')}.`);
    const recent = (((s.weddingJourney as any[]) || []).flatMap((d) => d.lines || []).slice(-3));
    if (recent.length) parts.push(`Recently: ${recent.join(' ')}`);
    return parts.length ? parts.join('\n') : undefined;
  })();

  return {
    query: userMessage.trim(),
    session_id: sessionId,
    wedding_context: weddingContextStr,
    wedding_id: (useAura.getState().wedding as any)?.id ?? undefined,  // joins replies → wedding actions (the recorder)
    wedding_destination: (useAura.getState().wedding as any)?.destination ?? undefined,  // eligibility country when the query names no place (Scoring Contract)
    turn_number: conversationHistory.filter((m) => m.from === 'user').length + 1,
    context: {
      previous_intent: previousIntent,
      previous_listing_ids: previousListingIds.length > 0 ? previousListingIds : undefined,
      previous_response_text: previousResponseText,
      consecutive_clarification_count: consecutiveClarificationCount > 0 ? consecutiveClarificationCount : undefined,
      active_comparison: activeComparison ?? undefined,
    },
    conversation_history: chatHistory.length > 0 ? chatHistory : undefined,
    device_type: (() => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
      return w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
    })(),
    anonymous_id: getOrCreateAnonId(),
    debug: auraDebugOn() || undefined,
  };
}

// Shared by both turn functions: maps the orchestrator's raw JSON payload to the
// client-facing shape sendMessage's .then() consumes. Used identically by the
// blocking and streaming paths so the streaming path cannot silently diverge in
// what it hands back — only how long it takes to arrive differs.
function mapAuraResult(result: Awaited<ReturnType<typeof queryAuraOrchestrator>>): AuraTurnResult {
  const recommendations: AuraRecommendations | null = result.recommendations.length > 0
    ? {
        items: result.recommendations.map((r) => ({
          id: r.listing_id,
          slug: r.listing.slug,
          type: (r.listing.category_slug === 'wedding-venues' || !r.listing.category_slug) ? 'venue' as const : 'vendor' as const,
          category: r.listing.category_slug ?? 'wedding-venues',
          categorySlug: r.listing.category_slug ?? 'wedding-venues',
          name: r.listing.name,
          city: r.listing.city ?? '',
          region: r.listing.region ?? '',
          regionSlug: r.listing.region_slug ?? null,
          country: r.listing.country ?? '',
          countrySlug: r.listing.country
            ? r.listing.country.toLowerCase().replace(/\s+/g, '-')
            : null,
          imgs: (r.listing.imgs_array && r.listing.imgs_array.length > 0)
            ? r.listing.imgs_array
            : (r.listing.hero_image_url ? [r.listing.hero_image_url] : []),
          videoUrl: r.listing.video_url ?? undefined,
          online: true,
          verified: r.listing.verified,
          lwdScore: r.scores.final_aura_score,
          scores: {
            intentMatch:           r.scores.intent_match,
            capacityFit:           r.scores.capacity_fit,
            editorialQuality:      r.scores.editorial_quality,
            behavioralPerformance: r.scores.behavioral_performance,
            semanticMatch:         r.scores.semantic_match,
            luxuryProfile:         r.scores.luxury_profile,
          },
          priceFrom: r.listing.price_from
            ? `${r.listing.price_currency ?? ''}${r.listing.price_from.toLocaleString()}`
            : null,
          capacity: r.listing.capacity_max ?? r.listing.capacity_min ?? null,
          tag: r.tags[0] ?? null,
          categoryLabel: categoryLabel(r.listing.category_slug ?? ''),
          rank: r.rank,
          reason: (r as any).reason ?? null,
          // Phase 3, concrete wedding-intelligence capabilities that matched
          // the explicit brief (exclusive use, civil ceremony, external catering…).
          matchReasons: Array.isArray((r as any).match_reasons) ? (r as any).match_reasons : [],
        })),
        summary: result.response_text,
        intent: result.intent as any,
      }
    : null;

  const _intent = result.intent as Record<string, unknown>;
  const _listingIds = result.recommendations.map((r) => r.listing_id);
  const _queryId = result.query_id;
  const _conversationMode = result.conversation_mode;
  const _requiresClarification = result.intent?.requires_clarification ?? false;
  const _followUpPrompts: string[] | undefined = Array.isArray(result.follow_up_prompts) && result.follow_up_prompts.length > 0
    ? result.follow_up_prompts
    : undefined;

  const _editorialContext = Array.isArray(result.editorial_context) && result.editorial_context.length > 0
    ? result.editorial_context
    : undefined;
  const _showcaseContext = Array.isArray(result.showcase_context) && result.showcase_context.length > 0
    ? result.showcase_context
    : undefined;
  const _realWeddingContext = Array.isArray(result.real_wedding_context) && result.real_wedding_context.length > 0
    ? result.real_wedding_context
    : undefined;

  return { text: result.response_text, recommendations, _intent, _listingIds, _queryId, _conversationMode, _requiresClarification, _followUpPrompts, _editorialContext, _showcaseContext, _realWeddingContext, _trace: result.trace, _reasoningId: result.reasoning_id, _activeComparison: (result as { active_comparison?: unknown }).active_comparison, _productContext: (result as { product_context?: unknown }).product_context, _productBrowse: (result as { product_browse?: unknown }).product_browse };
}

async function _generateAuraResponse(
  userMessage: string,
  conversationHistory: AuraMessage[],
  activeContext: AuraActiveContext,
  consecutiveClarificationCount: number = 0,
): Promise<AuraTurnResult> {
  if (!userMessage?.trim()) {
    return { text: "I'm listening...", recommendations: null };
  }

  try {
    const result = await queryAuraOrchestrator(
      buildOrchestratorRequest(userMessage, conversationHistory, consecutiveClarificationCount),
    );
    return mapAuraResult(result);
  } catch (error) {
    console.error('[AURA] orchestrator error:', error);
    return {
      text: "I couldn't complete that search just now — ask me again in a moment and I'll pick up right where we left off.",
      recommendations: null,
      _intent: undefined,
      _listingIds: undefined,
    };
  }
}

// ─── Streaming turn (project_aura_streaming_ux_improvement.md) ────────────────
// Only ever called when AURA_STREAMING_ENABLED is true (see above) — off by
// default. Sends the SAME request buildOrchestratorRequest produces for the
// blocking path, plus `stream: true`, to the SAME orchestrator URL, and resolves
// with the SAME AuraTurnResult shape via the SAME mapAuraResult — so sendMessage's
// .then() handler needs no awareness of which transport ran. While the stream is
// open it live-updates streamStage (real pipeline stage, not a fake timer) and
// streamingText (a word-paced reveal of the orchestrator's own already-validated
// response_text — never raw, unvalidated model output; validated by
// generateExplanation before index.ts's respond() ever reveals it).
//
// Resilience: any transport-level failure (network error, non-2xx, a malformed
// stream) falls back to a real blocking call via queryAuraOrchestrator for the
// SAME turn, not a canned error message — a couple never sees a broken exchange
// just because streaming had a bad moment. Combined with the server-side
// AURA_ENABLE_STREAMING kill switch (which streamAuraOrchestrator already
// handles transparently by treating a plain JSON response as an immediate
// 'done'), this is the rollback mechanism: a secret flip or a transient failure
// both degrade to today's exact behaviour, never to a broken turn.
async function _generateAuraResponseStreaming(
  userMessage: string,
  conversationHistory: AuraMessage[],
  activeContext: AuraActiveContext,
  consecutiveClarificationCount: number = 0,
): Promise<AuraTurnResult> {
  if (!userMessage?.trim()) {
    return { text: "I'm listening...", recommendations: null };
  }

  const requestBody = { ...buildOrchestratorRequest(userMessage, conversationHistory, consecutiveClarificationCount), stream: true };

  const fallbackToBlocking = async (): Promise<AuraTurnResult> => {
    try {
      const result = await queryAuraOrchestrator({ ...requestBody, stream: undefined } as Parameters<typeof queryAuraOrchestrator>[0]);
      return mapAuraResult(result);
    } catch (error) {
      console.error('[AURA] orchestrator error (post-streaming-fallback):', error);
      return {
        text: "I couldn't complete that search just now — ask me again in a moment and I'll pick up right where we left off.",
        recommendations: null,
        _intent: undefined,
        _listingIds: undefined,
      };
    }
  };

  return new Promise((resolve) => {
    let settled = false;
    streamAuraOrchestrator(AURA_STREAM_URL, requestBody as unknown as Record<string, unknown>, {
      onStage: (stage, label, tMs) => {
        useAura.setState({ streamStage: { stage, label, tMs } });
      },
      onToken: (_delta, textSoFar) => {
        useAura.setState({ streamingText: textSoFar });
      },
      onDone: (raw) => {
        if (settled) return;
        settled = true;
        resolve(mapAuraResult(raw as Awaited<ReturnType<typeof queryAuraOrchestrator>>));
      },
      onError: (message) => {
        if (settled) return;
        settled = true;
        console.error('[AURA] streaming orchestrator error, falling back to blocking:', message);
        fallbackToBlocking().then(resolve);
      },
    }).catch((err) => {
      if (settled) return;
      settled = true;
      console.error('[AURA] streaming transport error, falling back to blocking:', err);
      fallbackToBlocking().then(resolve);
    });
  });
}

// ─── OFF-TOPIC DETECTION (simple heuristic for telemetry) ──────────────────────
function detectOffTopicQuery(userMessage: string, auraResponse: string): boolean {
  const weddingKeywords = [
    'wedding',
    'venue',
    'destination',
    'ceremony',
    'reception',
    'couple',
    'bride',
    'groom',
    'marriage',
    'celebration',
    'italy',
    'spain',
    'bali',
    'france',
    'location',
    'wedding planner',
  ];

  const userLower = userMessage.toLowerCase();
  const responseLower = auraResponse.toLowerCase();

  // Check if either message contains wedding-related keywords
  const hasWeddingContext = weddingKeywords.some(
    (keyword) => userLower.includes(keyword) || responseLower.includes(keyword)
  );

  // If Aura mentions "wedding" in response but user didn't ask about it, likely off-topic
  return !hasWeddingContext && responseLower.includes('wedding');
}

// ─── Attribution helper (call at enquiry submit time) ─────────────────────────
// Returns Aura context to attach to leads. Only meaningful if conversationDepth > 0.

export interface AuraAttributionPayload {
  auraSessionId: string;
  conversationDepth: number;
  lastIntent: import('./auraCore.types').AuraIntent | null;
  intentSummary: string | null;
  queryId: string | null;
  conversationMode: string | null;
}

export function getEnquiryAttributionPayload(): AuraAttributionPayload {
  const state = useAura.getState();
  const conversationDepth = state.messages.filter((m) => m.from === 'user').length;
  return {
    auraSessionId:     state.session.id,
    conversationDepth,
    lastIntent:        state.intent,
    intentSummary:     state.intent?.summary ?? null,
    queryId:           state.queryId,
    conversationMode:  state.conversationMode,
  };
}

/**
 * Did Aura bring this couple to this listing — in THIS tab, or an earlier one?
 *
 * The live store only knows about the tab it is running in, and the most common
 * journey deliberately leaves that tab: "View Profile" on a recommendation card
 * opens the listing in a new one. Before the bridge existed, the enquiry form on
 * that page saw queryId=null, skipped the conversion event, and Aura was recorded
 * as having introduced nobody — 0 enquire events against 115 clicks.
 *
 * 'live'   the conversation is in this tab; strongest evidence.
 * 'bridge' Aura put this listing in front of them within the last 30 days.
 * null     nothing to claim. Callers must not guess — an enquiry we wrongly
 *          attribute costs more than one we miss, because it is the kind of
 *          error that makes a member distrust every other number we show them.
 */
export function getEnquiryAttribution(listingId: string | null): {
  auraSessionId: string | null;
  queryId: string | null;
  rank: number;
  source: 'live' | 'bridge' | null;
} {
  const live = getEnquiryAttributionPayload();
  if (live.conversationDepth > 0 && live.queryId) {
    return { auraSessionId: live.auraSessionId, queryId: live.queryId, rank: 0, source: 'live' };
  }
  const touch = listingId ? getAuraTouch(listingId) : null;
  if (touch) return { auraSessionId: touch.s, queryId: touch.q, rank: touch.r, source: 'bridge' };
  return { auraSessionId: null, queryId: null, rank: 0, source: null };
}
